<template>
    <div class="d-flex">
        <div class="avatar-cont mr-2" style="margin-left: -32px">
            <PersonAvatar :size="24" :person="item.person"/>
        </div>
        <div class="fill-width">
            <div class="activity-row">
                <div class="d-flex">
                    <div>
                         <span
                             :class="['comment-name font-weight-medium', current_account.id === item.person.id ? 'me' : '']"
                         >
                        {{ item.person | format_name }}
                    </span>
                        <span
                            v-if="item.type === TASK_LOG_TYPE.TASK_MESSAGE_FROM_PROJECT_EMAIL"
                            class="comment-type ml-1"
                        >
                            {{ $t('replied to project email') }}
                        </span>
                        <span
                            v-else-if="item.type === TASK_LOG_TYPE.TASK_MESSAGE_FROM_CLIENT_EMAIL"
                            class="comment-type ml-1"
                        >
                            {{ $t('replied to client email') }}
                        </span>
                        <span
                            v-else-if="item.type === TASK_LOG_TYPE.TASK_MESSAGE || item.type === TASK_LOG_TYPE.TASK_INCOMING_EMAIL"
                            class="comment-type ml-1"
                        >
                            {{ getMessageText(item) }}
                        </span>
                        <span v-else-if="item.type === TASK_LOG_TYPE.TASK_MESSAGE_FROM_EMAIL"
                              class="comment-type ml-1"
                        >
                            {{ getMessageFromEmailText(item) }}
                        </span>
                        <span v-else class="comment-type ml-1"> {{ $t("commented") }}</span>

                    </div>
                    <div class="ml-auto d-flex align-center">
                        <div class="caption g4--text mr-1">
                            {{ item.created_at | format_datetime }}
                        </div>
                        <v-tooltip bottom>
                            <template #activator="{ on }">
                                <icon-button
                                    :on="on"
                                    icon="Reply"
                                    x-small
                                    icon-size="16"
                                    @click="$emit('reply', item.type)"
                                />
                            </template>
                            {{ $t('Reply') }}
                        </v-tooltip>

                        <action-menu
                            :items="actionItems"
                            :icon-size="16"
                            @action="actionItems[$event].action(item)"
                        >
                            <template v-slot:activator="{ on }">
                                <icon-button
                                    :on="on"
                                    x-small
                                    icon="MoreVertical"
                                />
                            </template>
                        </action-menu>
                    </div>
                </div>
            </div>
            <TaskEditCommentControl
                v-if="isEditMode"
                :log="item"
                :task-id="taskId"
                @close="isEditMode = false"
            ></TaskEditCommentControl>
            <EmailBody
                v-else
                :class="['grey-content fill-width my-1']"
                :item="item"
                @download="download"
            />
        </div>
    </div>
</template>

<script>
import { $t } from "@/i18n";
import PersonAvatar from "../../../../components/PersonAvatar";
import AccountModule from "../../../../store/modules/account";
import TaskEditCommentControl from "../../TaskEditCommentControl";
import {computed, ref, watch} from "@vue/composition-api";
import DialogModule from "../../../../store/modules/dialog";
import TaskModule from "../../../../store/modules/task";
import {TASK_LOG_TYPE} from "@/helpers/constants";
import Footer from "../Footer"
import errorDialog from "@/views/ErrorDialog/errorDialog"
import MiniAttachmentChip from "@/components/MiniAttachmentChip.vue";
import {downloadFile} from "@/helpers/helpers";
import Parser from "node-email-reply-parser/lib/Parser"
import EmailBody from "@/components/Emailing/EmailBody/Index.vue"

export default {
    name: "MessageRow",
    components: {
        PersonAvatar,
        TaskEditCommentControl,
        Footer,
        MiniAttachmentChip,
        EmailBody
    },
    props: {
        item: {
            type: Object,
            required: true
        },
        taskId: {
            type: Number,
            required: true
        }
    },
    setup(props, ctx) {
        const dialogModule = DialogModule();
        const { deleteTaskComment } = TaskModule();
        const { current_account } = AccountModule();
        const showHidden = ref(false);
        const isEditMode = ref(false);
        const messageExpandCollapseText = ref($t('Show trimmed content'))

        const delete_comment = function (log) {
            dialogModule.setConfirmOptions({
                title: $t("Are you sure to delete this comment?"),
                confirm_text: $t("Delete"),
                cancel_text: $t("Cancel"),
                action: () => delete_comment_confirmed(log)
            });
            dialogModule.openConfirmDialog();
        };
        const delete_comment_confirmed = function (log) {
            deleteTaskComment(props.taskId, log.id).catch(function (ex) {
                errorDialog.showError(ex);
            });
        };

        const attachmentToLink = (str) => {
            return str.replace(/\[File:\d+\|[^\]]+\]/gm, function (m) {
                let arr = m.substr(6, m.length - 7).split("|");
                return '<a href="/api/task_attachment/{0}">{1}</a>'.format(arr[0], arr[1]);
            });
        }

        const actionItems = computed(() => {
            let items = [
                {
                    text: $t('Edit'),
                    action: () => isEditMode.value = true
                },
                {
                    text: $t('Delete'),
                    action: delete_comment
                },
            ]

            return items
        })

        const download = async (file) => {
            try {
                await downloadFile('task_attachment', file)
            } catch (e) {
                errorDialog.showError(e)
            }
        }

        const parsedMessage = computed(() => {
            const QUOTE_HEADERS_REGEX = [
                /^Kontakt\s[\S\s]+\(&lt;[\w.-]+@[\w.-]+&gt;\)\skirjutas\skuupäeval\s[ETNKRP],\s\d{1,2}\.\s(?:jaanuar|veebruar|märts|aprill|mai|juuni|juuli|august|september|oktoober|november|detsember)/m,
                /^(?:&gt;)*\s*(?:Kontakt\s[\S\s]+\skirjutas\skuupäeval\s)?[ETNKRP],\s\d{1,2}\.\s(?:jaanuar|veebruar|märts|aprill|mai|juuni|juuli|august|september|oktoober|november|detsember)\n\d{4}\skell\s\d{1,2}:\d{2}\s.+\skirjutas:$/m,
                /^[ETNKRP],\s\d{1,2}\.\s(?:jaanuar|veebruar|märts|aprill|mai|juuni|juuli|august|september|oktoober|november|detsember)\s\d{4}\s\d{1,2}:\d{2}\s.+\skirjutas:$/,
                /^(?:&gt;)*\s*(On(?:(?!.*On\b|\bwrote:)[\s\S])+wrote:)$/m, // On DATE, NAME <EMAIL> wrote:
                /^(?:&gt;)*\s*(Le(?:(?!.*Le\b|\bécrit:)[\s\S])+écrit(\s|\xc2\xa0):)$/m, // On DATE, NAME <EMAIL> wrote:
                /^(?:&gt;)*\s*(El(?:(?!.*El\b|\bescribió:)[\s\S])+escribi(ó|eron):)$/m, // On DATE, NAME <EMAIL> wrote:
                /^(?:&gt;)*\s*(Il(?:(?!.*Il\b|\bscritto:)[\s\S])+scritto:)$/m, // On DATE, NAME <EMAIL> wrote:
                /^(?:&gt;)*\s*(Op\s[\S\s]+?(schreef|geschreven)[\S\s]+:)$/m, // Op DATE schreef NAME <EMAIL>:, Op DATE heeft NAME <EMAIL> het volgende geschreven:
                /^(?:&gt;)*\s*(Em(?:(?!.*Em\b|\bescreveu:)[\s\S])+escreveu:)$/m, // Em DATE, NAME <EMAIL> escreveu:
                /^(?:&gt;)*\s*((W\sdniu|Dnia)\s[\S\s]+?(pisze|napisał(\(a\))?):)$/mu, // W dniu DATE, NAME <EMAIL> pisze|napisał:
                /^(?:&gt;)*\s*(Den\s.+\sskrev\s.+:)$/m, // Den DATE skrev NAME <EMAIL>:
                /^(?:&gt;)*\s*(Am\s.+\sum\s.+\sschrieb\s.+:)$/m, // Am DATE um TIME schrieb NAME:
                /^(在[\S\s]+写道：)$/m, // > 在 DATE, TIME, NAME 写道：
                /^(20[0-9]{2}\..+\s작성:)$/m, // DATE TIME NAME 작성:
                /^(20[0-9]{2}\/.+のメッセージ:)$/m, // DATE TIME、NAME のメッセージ:
                /^(.+\s<.+>\sschrieb:)$/m, // NAME <EMAIL> schrieb:
                /^(?:&gt;)*\s*(From\s?:.+\s?(\[|<).+(\]|>))/mu, // "From: NAME <EMAIL>" OR "From : NAME <EMAIL>" OR "From : NAME<EMAIL>"(With support whitespace before start and before <)
                /^(?:&gt;)*\s*(De\s?:.+\s?(\[|<).+(\]|>))/mu, // "De: NAME <EMAIL>" OR "De : NAME <EMAIL>" OR "De : NAME<EMAIL>"  (With support whitespace before start and before <)
                /^(?:&gt;)*\s*(Van\s?:.+\s?(\[|<).+(\]|>))/mu, // "Van: NAME <EMAIL>" OR "Van : NAME <EMAIL>" OR "Van : NAME<EMAIL>"  (With support whitespace before start and before <)
                /^(?:&gt;)*\s*(Da\s?:.+\s?(\[|<).+(\]|>))/mu, // "Da: NAME <EMAIL>" OR "Da : NAME <EMAIL>" OR "Da : NAME<EMAIL>"  (With support whitespace before start and before <)
                /^(20[0-9]{2}-(?:0?[1-9]|1[012])-(?:0?[0-9]|[1-2][0-9]|3[01]|[1-9])\s[0-2]?[0-9]:\d{2}\s[\S\s]+?:)$/m, // 20YY-MM-DD HH:II GMT+01:00 NAME <EMAIL>:
                /^(?:&gt;)*\s*([a-z]{3,4}\.[\s\S]+\sskrev[\s\S]+:)$/m, // DATE skrev NAME <EMAIL>:
                /^(?:&gt;)*\s*(El(?:(?!^>*\s*El\b|\bha escrit:).){0,1000}ha escrit:)$/ms, // El DATE, NAME <EMAIL> ha escrit:
            ];

            const SIGNATURE_REGEX = /(?:^\s*--|^\s*__|^-\w|^-- $)|(?:^Sent from my (?:\s*\w+){1,4}$)|(?:^={30,}$)$/;

            const QUOTE_REGEX = /(&gt;)+/;

            const parser = new Parser(SIGNATURE_REGEX, QUOTE_REGEX, QUOTE_HEADERS_REGEX)

            return parser.parse(props.item.message)
        })

        const parsedFragments = computed(() => {
            const allFragments = parsedMessage.value.getFragments()
            return allFragments.slice(1)
        })

        const visibleText = computed(() => {
            return parsedMessage.value.getVisibleText()
        })

        const hasHiddenFragments = computed(() => {
            for (const fragment of parsedFragments.value) {
                if (fragment.isHidden()) {
                    return true
                }
            }

            return false
        })

        const getMessageText = (item) => {
            const message = $t("replied to client portal email");

            if (!item.person_id) {
                const from_email = item?.person?.from_email;
                return from_email ? `(${from_email}) ${message}` : message;
            }

            return item?.person?.from_email ? message : $t("sent a message");
        }

        const getMessageFromEmailText = (item) => {
            const message = $t("replied to task email");

            if (!item.person_id) {
                const from_email = item?.person?.from_email;
                return from_email ? `(${from_email}) ${message}` : message;
            }

            return item?.person?.from_email ? message : $t("sent a message");
        }

        watch(() => showHidden.value, (newVal) => {
            if (newVal) {
                messageExpandCollapseText.value = $t('Hide expanded content')
            } else {
                 messageExpandCollapseText.value = $t('Show trimmed content')
            }
        })


        return {
            current_account,
            delete_comment,
            attachmentToLink,
            isEditMode,
            actionItems,
            TASK_LOG_TYPE,
            download,
            parsedFragments,
            visibleText,
            showHidden,
            hasHiddenFragments,
            getMessageText,
            getMessageFromEmailText,
            messageExpandCollapseText,
        }
    }
}
</script>

<style scoped>
.hidden-button ::v-deep {
    .v-btn {
        height: 24px !important;
    }
}
</style>